import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usuariosService, uploadService } from '../services/api'
import './Perfil.css'

function Perfil() {
    const { usuario, carregando, isLogado, logout, atualizarUsuario } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [mensagem, setMensagem] = useState({ tipo: '', texto: '' })
    const [editando, setEditando] = useState(false)
    const [totalAnuncios, setTotalAnuncios] = useState(0)
    const [vendasRealizadas, setVendasRealizadas] = useState(0)

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        whatsapp: '',
        cpfCnpj: '',
        endereco: '',
        cidade: '',
        estado: '',
        bio: ''
    })

    useEffect(() => {
        if (carregando) return

        if (!isLogado) {
            navigate('/login')
            return
        }

        // Carregar dados do usuário
        if (usuario) {
            setFormData({
                nome: usuario.nome || '',
                email: usuario.email || '',
                telefone: usuario.telefone || '',
                whatsapp: usuario.whatsapp || '',
                cpfCnpj: usuario.cpfCnpj || '',
                endereco: usuario.endereco || '',
                cidade: usuario.cidade || '',
                estado: usuario.estado || '',
                bio: usuario.bio || ''
            })

            // Carregar estatísticas
            const carregarEstatisticas = async () => {
                try {
                    const res = await usuariosService.buscarPerfil(usuario.id)
                    if (res.usuario) {
                        setTotalAnuncios(res.usuario.totalAnuncios || 0)
                        setVendasRealizadas(res.usuario.vendasRealizadas || 0)
                    }
                } catch (error) {
                    console.error('Erro ao carregar estatísticas:', error)
                }
            }
            carregarEstatisticas()
        }
    }, [isLogado, usuario, navigate])

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }

    const handleFotoChange = async (e) => {
        const file = e.target.files[0]
        if (!file) return

        try {
            setMensagem({ tipo: 'info', texto: 'Enviando foto...' })
            const res = await uploadService.uploadImagem(file)

            if (res.filename || res.url) {
                const fotoUrl = res.url || `/uploads/${res.filename}`
                await usuariosService.atualizarPerfil({
                    nome: formData.nome,
                    telefone: formData.telefone,
                    fotoPerfil: fotoUrl
                })

                atualizarUsuario({ fotoPerfil: fotoUrl })
                setMensagem({ tipo: 'success', texto: 'Foto de perfil atualizada!' })
            }
        } catch (error) {
            setMensagem({ tipo: 'error', texto: error.message || 'Erro ao enviar foto' })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSalvando(true)
        setMensagem({ tipo: '', texto: '' })

        try {
            await usuariosService.atualizarPerfil({
                nome: formData.nome,
                telefone: formData.telefone,
                whatsapp: formData.whatsapp,
                endereco: formData.endereco,
                cidade: formData.cidade,
                estado: formData.estado,
                bio: formData.bio
            })

            atualizarUsuario({
                nome: formData.nome,
                telefone: formData.telefone,
                whatsapp: formData.whatsapp,
                endereco: formData.endereco,
                cidade: formData.cidade,
                estado: formData.estado,
                bio: formData.bio
            })

            setMensagem({ tipo: 'success', texto: 'Perfil atualizado com sucesso!' })
            setEditando(false)

        } catch (error) {
            setMensagem({ tipo: 'error', texto: error.message || 'Erro ao atualizar perfil' })
        } finally {
            setSalvando(false)
        }
    }

    const getVerificacaoStatus = () => {
        // Se perfil completo = verificado
        if (usuario?.perfilCompleto || usuario?.statusVerificacao === 'aprovado') {
            return { classe: 'verificado', texto: '✓ Verificado', icone: '🛡️' }
        }
        return { classe: 'pendente', texto: 'Não verificado', icone: '⚠️' }
    }

    const getBadges = () => {
        const badges = []

        // Badge de Verificação (Sempre visível)
        badges.push({
            classe: verificacao.classe,
            texto: verificacao.texto,
            icone: verificacao.icone,
            tipo: 'verificacao'
        })

        // Se for Motorista
        const ehMotorista = usuario?.tipo === 'motorista' || 
                           (usuario?.tipos_habilitados && usuario.tipos_habilitados.includes('motorista')) ||
                           (usuario?.tiposHabilitados && usuario.tiposHabilitados.includes('motorista'))
        
        if (ehMotorista) {
            badges.push({
                classe: 'badge-motorista',
                texto: 'Motorista',
                icone: '🚚',
                cor: '#3b82f6'
            })
        }

        // Se for Vendedor Premium (Plano Pro)
        const isPremium = usuario?.assinatura_ilimitada === true && 
                          usuario?.expiracao_assinatura && 
                          new Date(usuario.expiracao_assinatura) > new Date()

        if (isPremium) {
            badges.push({
                classe: 'badge-premium',
                texto: 'Plano Pro',
                icone: '⭐',
                cor: '#f59e0b'
            })
        } else if (usuario?.tipo === 'vendedor' || (usuario?.tipos_habilitados && usuario.tipos_habilitados.includes('vendedor'))) {
            badges.push({
                classe: 'badge-vendedor',
                texto: 'Vendedor',
                icone: '🏷️',
                cor: '#22c55e'
            })
        }

        // Créditos Avulsos
        const creditos = parseInt(usuario?.creditos_anuncio) || 0
        if (creditos > 0) {
            badges.push({
                classe: 'badge-creditos',
                texto: `${creditos} Crédito${creditos > 1 ? 's' : ''}`,
                icone: '🎫',
                cor: '#8b5cf6'
            })
        }

        return badges
    }

    const verificacao = getVerificacaoStatus()

    if (carregando) {
        return (
            <div className="perfil-page">
                <div className="loading-container">
                    <p>Carregando informações...</p>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="perfil-page">
                <div className="loading-container">
                    <div className="skeleton" style={{ width: '100%', height: '200px' }}></div>
                </div>
            </div>
        )
    }

    return (
        <div className="perfil-page">
            <div className="perfil-container">
                {/* Header do Perfil */}
                <div className="perfil-header">
                    <div className="perfil-avatar-container">
                        <div className="perfil-avatar">
                            {usuario?.fotoPerfil ? (
                                <img src={usuario.fotoPerfil?.startsWith('http') ? usuario.fotoPerfil : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${usuario.fotoPerfil}`} alt={usuario.nome} className="avatar-img" />
                            ) : (
                                <div className="avatar-placeholder">
                                    {usuario?.nome?.charAt(0).toUpperCase()}
                                </div>
                            )}
                        </div>
                        <label className="upload-avatar-btn">
                            📷 Trocar foto de perfil
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFotoChange}
                                style={{ display: 'none' }}
                            />
                        </label>
                    </div>
                    <div className="perfil-info">
                        <h1>{usuario?.nome}</h1>
                        <div className="perfil-badges">
                            {getBadges().map((badge, idx) => (
                                <span 
                                    key={idx}
                                    className={`badge-item ${badge.classe}`}
                                    style={badge.cor ? { background: badge.cor, color: '#fff', border: 'none' } : {}}
                                >
                                    {badge.icone} {badge.texto}
                                </span>
                            ))}
                        </div>
                        <p className="perfil-membro">
                            Membro desde {new Date(usuario?.criado_em || Date.now()).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                        </p>
                    </div>
                </div>

                {/* Mensagens */}
                {mensagem.texto && (
                    <div className={`alert alert-${mensagem.tipo}`}>
                        {mensagem.texto}
                    </div>
                )}

                {/* Cards de Informação */}
                <div className="perfil-grid">
                    {/* Dados Pessoais */}
                    <div className="perfil-card">
                        <div className="card-header">
                            <h2>📋 Dados Pessoais</h2>
                            {!editando && (
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setEditando(true)}
                                >
                                    ✏️ Editar
                                </button>
                            )}
                        </div>

                        {editando ? (
                            <form onSubmit={handleSubmit} className="perfil-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Nome completo</label>
                                        <input
                                            type="text"
                                            name="nome"
                                            className="form-input"
                                            value={formData.nome}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Telefone</label>
                                        <input
                                            type="tel"
                                            name="telefone"
                                            className="form-input"
                                            placeholder="(00) 00000-0000"
                                            value={formData.telefone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">WhatsApp (para negócios)</label>
                                        <input
                                            type="tel"
                                            name="whatsapp"
                                            className="form-input"
                                            placeholder="(00) 00000-0000"
                                            value={formData.whatsapp}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Cidade</label>
                                        <input
                                            type="text"
                                            name="cidade"
                                            className="form-input"
                                            placeholder="Sua cidade"
                                            value={formData.cidade}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Estado</label>
                                        <select
                                            name="estado"
                                            className="form-input"
                                            value={formData.estado}
                                            onChange={handleChange}
                                        >
                                            <option value="">Selecione</option>
                                            <option value="AC">Acre</option>
                                            <option value="AL">Alagoas</option>
                                            <option value="AP">Amapá</option>
                                            <option value="AM">Amazonas</option>
                                            <option value="BA">Bahia</option>
                                            <option value="CE">Ceará</option>
                                            <option value="DF">Distrito Federal</option>
                                            <option value="ES">Espírito Santo</option>
                                            <option value="GO">Goiás</option>
                                            <option value="MA">Maranhão</option>
                                            <option value="MT">Mato Grosso</option>
                                            <option value="MS">Mato Grosso do Sul</option>
                                            <option value="MG">Minas Gerais</option>
                                            <option value="PA">Pará</option>
                                            <option value="PB">Paraíba</option>
                                            <option value="PR">Paraná</option>
                                            <option value="PE">Pernambuco</option>
                                            <option value="PI">Piauí</option>
                                            <option value="RJ">Rio de Janeiro</option>
                                            <option value="RN">Rio Grande do Norte</option>
                                            <option value="RS">Rio Grande do Sul</option>
                                            <option value="RO">Rondônia</option>
                                            <option value="RR">Roraima</option>
                                            <option value="SC">Santa Catarina</option>
                                            <option value="SP">São Paulo</option>
                                            <option value="SE">Sergipe</option>
                                            <option value="TO">Tocantins</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Endereço</label>
                                    <input
                                        type="text"
                                        name="endereco"
                                        className="form-input"
                                        placeholder="Rua, número, bairro..."
                                        value={formData.endereco}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Sobre mim</label>
                                    <textarea
                                        name="bio"
                                        className="form-input form-textarea"
                                        placeholder="Conte um pouco sobre você ou sua fazenda..."
                                        value={formData.bio}
                                        onChange={handleChange}
                                        rows={4}
                                    />
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setEditando(false)}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className={`btn btn-primary ${salvando ? 'loading' : ''}`}
                                        disabled={salvando}
                                    >
                                        {salvando ? 'Salvando...' : 'Salvar Alterações'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="perfil-dados">
                                <div className="dado-item">
                                    <span className="dado-label">E-mail</span>
                                    <span className="dado-valor">{usuario?.email}</span>
                                </div>
                                <div className="dado-item">
                                    <span className="dado-label">CPF/CNPJ</span>
                                    <span className="dado-valor">{usuario?.cpfCnpj || 'Não informado'}</span>
                                </div>
                                <div className="dado-item">
                                    <span className="dado-label">Telefone</span>
                                    <span className="dado-valor">{usuario?.telefone || 'Não informado'}</span>
                                </div>
                                <div className="dado-item">
                                    <span className="dado-label">WhatsApp</span>
                                    <span className="dado-valor">
                                        {usuario?.whatsapp ? (
                                            <a 
                                                href={`https://wa.me/${usuario.whatsapp.replace(/\D/g, '').startsWith('55') ? usuario.whatsapp.replace(/\D/g, '') : '55' + usuario.whatsapp.replace(/\D/g, '')}`} 
                                                target="_blank" 
                                                rel="noreferrer"
                                                style={{ color: '#22c55e', fontWeight: '600', textDecoration: 'underline' }}
                                            >
                                                {usuario.whatsapp} (Abrir WhatsApp)
                                            </a>
                                        ) : 'Não informado'}
                                    </span>
                                </div>
                                <div className="dado-item">
                                    <span className="dado-label">Localização</span>
                                    <span className="dado-valor">
                                        {usuario?.cidade && usuario?.estado
                                            ? `${usuario.cidade}, ${usuario.estado}`
                                            : 'Não informado'}
                                    </span>
                                </div>
                                {usuario?.bio && (
                                    <div className="dado-item dado-bio">
                                        <span className="dado-label">Sobre</span>
                                        <span className="dado-valor">{usuario.bio}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dados do Veículo (Apenas para Motoristas) */}
                    {(usuario?.tipo === 'motorista' || usuario?.tiposHabilitados?.includes('motorista') || usuario?.tipos_habilitados?.includes('motorista')) && (
                        <div className="perfil-card">
                            <div className="card-header">
                                <h2>🚚 Dados do Veículo</h2>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => navigate('/adicionar-tipo?tipo=motorista&modo=editar')}
                                >
                                    ✏️ Gerenciar Veículo
                                </button>
                            </div>
                            <div className="perfil-dados">
                                <div className="dado-item">
                                    <span className="dado-label">Tipo de Veículo</span>
                                    <span className="dado-valor">{usuario?.tipoVeiculo || usuario?.tipo_veiculo || 'Não informado'}</span>
                                </div>
                                <div className="dado-item">
                                    <span className="dado-label">Placa</span>
                                    <span className="dado-valor">{usuario?.placaVeiculo || usuario?.placa_veiculo || 'Não informada'}</span>
                                </div>
                                {(usuario?.capacidadeGado || usuario?.capacidade_gado) && (
                                    <div className="dado-item">
                                        <span className="dado-label">Capacidade (Gado)</span>
                                        <span className="dado-valor">{usuario?.capacidadeGado || usuario?.capacidade_gado} cabeças</span>
                                    </div>
                                )}
                                {usuario?.fotoVeiculo || usuario?.foto_veiculo ? (
                                    <div className="dado-item" style={{ marginTop: '10px' }}>
                                        <span className="dado-label">Foto do Veículo</span>
                                        <img 
                                            src={(usuario.fotoVeiculo || usuario.foto_ve_iculo)?.startsWith('http') ? (usuario.fotoVeiculo || usuario.foto_ve_iculo) : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${usuario.fotoVeiculo || usuario.foto_ve_iculo}`} 
                                            alt="Veículo" 
                                            style={{ width: '100%', maxHeight: '150px', borderRadius: '8px', marginTop: '5px', objectFit: 'cover' }} 
                                        />
                                    </div>
                                ) : (
                                    <div className="dado-item">
                                        <span className="dado-label">Foto do Veículo</span>
                                        <span className="dado-valor" style={{ color: '#ef4444' }}>⚠️ Nenhuma foto enviada</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Estatísticas */}
                    <div className="perfil-card">
                        <div className="card-header">
                            <h2>📊 Estatísticas</h2>
                        </div>
                        <div className="stats-grid">
                            <div className="stat-item">
                                <span className="stat-valor">{totalAnuncios}</span>
                                <span className="stat-label">Anúncios Ativos</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-valor">{vendasRealizadas}</span>
                                <span className="stat-label">Vendas Realizadas</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-valor">0</span>
                                <span className="stat-label">Avaliações</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-valor">⭐ -</span>
                                <span className="stat-label">Nota Média</span>
                            </div>
                        </div>
                    </div>

                    {/* Segurança */}
                    <div className="perfil-card">
                        <div className="card-header">
                            <h2>🔒 Segurança</h2>
                        </div>
                        <div className="seguranca-opcoes">
                            <button className="btn btn-secondary btn-block">
                                🔑 Alterar Senha
                            </button>
                            <button
                                className="btn btn-outline btn-block btn-danger"
                                onClick={logout}
                            >
                                🚪 Sair da Conta
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Perfil
